import { Fragment } from 'react';

import { cadenceStepLeftListFromRawSteps } from '@poseidon/shared-utils/cadenceStep';

import type { ContactCardOrgRowOrg } from './ContactCardOrgRowPure';
import type { Stage } from './Input2';
import { cn } from './cn';
import ContactCardActions from './ContactCardActions';
import { ContactCardCadenceActions } from './ContactCardCadenceActions';
import ContactCardNameRowPure from './ContactCardNameRowPure';
import ContactCardOrgRowPure from './ContactCardOrgRowPure';
import { formatStageDuration } from './contactUtils';
import { Button, StageButton, Tooltip } from './Input2';
import { StepIcon } from './StepIcon';
import { Arrows, Communication, General, Users } from './StrokeIcons';

type CadenceState =
  | 'not-started'
  | 'active'
  | 'paused'
  | 'finished'
  | 'interrupted'
  | 'action-required'
  | 'responded'
  | 'deleted'
  | 'researching'
  | 'pending-review'
  | 'rejected'
  | 'contact_unavailable';

// Local flat mirror of the DB `cadence_step_option` enum (UI components must not import
// pg-gen types). Fork arms use 'three'..'six'; keep in sync with the enum.
type CadenceStepOption = 'one' | 'two' | 'three' | 'four' | 'five' | 'six';

type CadenceStepState =
  | 'not-started'
  | 'interrupted'
  | 'skipped'
  | 'finished'
  | 'canceled'
  | 'action-required'
  | 'responded'
  | 'contact_unavailable';

type CadenceStepType =
  | 'message'
  | 'connect'
  | 'inmail'
  | 'visit'
  | 'follow'
  | 'email'
  | 'reminder'
  | 'call'
  | 'delay'
  | 'likeRecentPosts'
  | 'wait_for_connect'
  | 'voice_note'
  | 'sift'
  | 'switch_campaign'
  | 'switch';

export type { ContactCardOrgRowOrg } from './ContactCardOrgRowPure';
export type { Stage } from './Input2';

export interface ContactCardBodyCadenceStep {
  id: string;
  parent_id: string | null;
  type: CadenceStepType;
  state: CadenceStepState;
  option: CadenceStepOption;
  archived: boolean | null;
  computedError?: {
    default?: string;
    body2?: string;
    subject?: string;
  };
}

type ContactCardBodyPureProps = {
  size?: 'default' | 'pipeline';

  name: string;
  honorificTitle?: string | null;
  liOpenLink?: boolean | null;
  /** Whether the contact is a 1st-degree LinkedIn connection. Hides the InMail icon when true. */
  liConnected?: boolean | null;
  disqualified?: boolean;

  orgName?: string | null;
  orgPictureUrl?: string | null;
  title?: string;

  tags?: { id: string; name: string }[] | null;
  triggeredSignals?: { bucketKey: string; bucketName: string; reason: string | null }[] | null;

  hasResponse?: boolean;
  cadenceName?: string;
  cadenceIsActive?: boolean;
  cadenceState?: CadenceState;
  cadenceRunning?: boolean;
  cadenceNextStepTypes?: CadenceStepType[];
  cadenceInterruptMessage?: string | null;
  firstReminderBody?: string | null;
  firstReminderDate?: Date;
  canNotRunStepTypes?: Set<CadenceStepType> | undefined;
  manuallyRunning?: boolean;

  cadenceSteps?: ContactCardBodyCadenceStep[] | null;
  show?: 'reminder' | 'step';

  stages?: Stage[];
  stageId?: string;
  stagesLoading?: boolean;
  stageUpdating?: boolean;
  onStageChange?: (stageId: string | null) => void;
  /** When the contact entered its current stage; renders a compact "time in stage" badge (e.g. 12d). */
  stageEnteredAt?: Date | string | null;

  phone: string | null | undefined;
  phoneVerified: boolean | null | undefined;
  professionalEmail: string | null | undefined;
  professionalEmailVerified: boolean | null | undefined;
  onCall?: () => void;
};

export default function ContactCardBodyPure({
  size = 'default',
  name,
  honorificTitle,
  liOpenLink,
  liConnected,
  disqualified,
  orgName,
  orgPictureUrl,
  title,
  tags,
  triggeredSignals,
  hasResponse,
  cadenceName,
  cadenceIsActive,
  cadenceState,
  cadenceRunning,
  cadenceNextStepTypes,
  cadenceInterruptMessage,
  firstReminderBody,
  firstReminderDate,
  canNotRunStepTypes,
  manuallyRunning,
  cadenceSteps,
  show,
  stages,
  stageId,
  stagesLoading,
  stageUpdating,
  onStageChange,
  stageEnteredAt,
  phone,
  phoneVerified,
  professionalEmail,
  professionalEmailVerified,
  onCall,
}: ContactCardBodyPureProps) {
  const stepsList = cadenceSteps ? cadenceStepLeftListFromRawSteps(cadenceSteps) : undefined;
  const actionRequired = cadenceState === 'action-required' || cadenceState === 'responded';
  // Prefer the canonical `default` (error_msg) reason, but fall back to the
  // body/subject template errors (e.g. an unresolved personalization variable),
  // which is the only place the reason is recorded for that failure mode.
  const interruptedComputedError = cadenceSteps?.find((s) => s.state === 'interrupted')?.computedError;
  const errorMessage =
    interruptedComputedError?.default ?? interruptedComputedError?.body2 ?? interruptedComputedError?.subject;
  const stageDuration = stageEnteredAt != null ? formatStageDuration(stageEnteredAt) : '';

  return (
    <div
      className={cn(
        'flex w-0 shrink grow flex-col justify-between text-slate-800 dark:text-white',
        size === 'pipeline' && 'gap-1 text-xs',
      )}>
      <div className={cn('flex justify-between gap-1.5 overflow-hidden', size === 'pipeline' && 'gap-1')}>
        <div className={cn('flex w-0 grow flex-col gap-1.5', size === 'pipeline' && 'gap-1')}>
          <ContactCardNameRowPure
            name={name}
            honorificTitle={honorificTitle}
            liOpenLink={liOpenLink}
            liConnected={liConnected}
            cadenceName={cadenceName}
            // On pipeline cards the long campaign chip moves to its own row below
            // (see the cadence row) so the contact name gets the full card width.
            hideCadenceName={size === 'pipeline'}
            cadenceIsActive={cadenceIsActive}
            cadenceState={cadenceState}
            size={size}
            disqualified={disqualified}
          />
          <ContactCardOrgRowPure orgName={orgName} orgPictureUrl={orgPictureUrl} title={title} size={size} />
          {/* Pipeline: cadence chip + status badge on their own wrapping row, under
              the name/org — so neither competes with the name for horizontal space.
              ContactCardCadenceActions self-hides when there's no badge, so an empty
              row just collapses (no visible gap). */}
          {size === 'pipeline' && (
            <div className="flex flex-wrap items-center gap-1 overflow-hidden">
              {cadenceName && (
                <Button
                  size="2xs"
                  readOnly
                  color={cadenceState === 'finished' ? 'blue' : undefined}
                  icon={cadenceState === 'finished' ? <General.Check /> : undefined}
                  tooltip={cadenceState === 'finished' ? 'Campaign finished' : undefined}>
                  {cadenceName}
                </Button>
              )}
              <ContactCardCadenceActions
                hasResponse={hasResponse}
                cadenceState={cadenceState}
                cadenceRunning={cadenceRunning}
                cadenceNextStepTypes={cadenceNextStepTypes}
                cadenceInterruptMessage={cadenceInterruptMessage}
                firstReminderBody={firstReminderBody}
                firstReminderDate={firstReminderDate}
                canNotRunStepTypes={canNotRunStepTypes}
                manuallyRunning={manuallyRunning}
                errorMessage={errorMessage}
                // Finished state is shown on the campaign chip above (blue + check),
                // so suppress the duplicate FINISHED pill whenever we have a chip.
                hideFinished={Boolean(cadenceName)}
              />
            </div>
          )}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-0.5 overflow-hidden">
              {tags?.slice(0, 3).map((tag) => (
                <Button key={tag.id} size="2xs" className="pointer-events-none">
                  {tag.name}
                </Button>
              ))}
              {tags.length > 3 && (
                <Button size="2xs" className="pointer-events-none">
                  +{tags.length - 3}
                </Button>
              )}
            </div>
          )}
        </div>
        {/* Default cards keep the cadence status badge as a right-hand column. */}
        {size !== 'pipeline' && (
          <ContactCardCadenceActions
            hasResponse={hasResponse}
            cadenceState={cadenceState}
            cadenceRunning={cadenceRunning}
            cadenceNextStepTypes={cadenceNextStepTypes}
            cadenceInterruptMessage={cadenceInterruptMessage}
            firstReminderBody={firstReminderBody}
            firstReminderDate={firstReminderDate}
            canNotRunStepTypes={canNotRunStepTypes}
            manuallyRunning={manuallyRunning}
            errorMessage={errorMessage}
          />
        )}
        {onStageChange && (
          <StageButton
            size="xs"
            stages={stages}
            stageId={stageId}
            disabled={stagesLoading}
            onChange={onStageChange}
            onClick={(e) => e.stopPropagation()}
            status={stageUpdating ? 'updating' : undefined}
            icon={<Users.Users01 />}
            className="ml-1"
          />
        )}
      </div>
      <div className={cn('flex min-h-4.5 gap-0.5 overflow-hidden group-[]:', size === 'pipeline' && 'min-h-2')}>
        {show === 'reminder' && firstReminderBody && (
          <div
            data-testid="reminder-body"
            className="rounded-xs px-1.5 py-0.5 bg-indigo-100 text-xs tracking-tight text-indigo-700 dark:bg-indigo-800 dark:text-white">
            {firstReminderBody}
          </div>
        )}
        {show !== 'reminder' && (
          <div className="flex items-center gap-0.5">
            {stepsList?.map((step) => {
              if (step.type === 'delay' || step.type === 'wait_for_connect') {
                return <Arrows.ChevronRight key={step.id} className="size-3 shrink-0 stroke-3" />;
              }
              const triggerStep = step.triggerStep;
              const showTriggerStep =
                triggerStep != null && triggerStep.type !== 'delay' && triggerStep.type !== 'wait_for_connect';
              return (
                <Fragment key={step.id}>
                  <Tooltip tooltip={`${step.type} step: ${step.state}`}>
                    <StepIcon iconType={step.type} state={step.state} />
                  </Tooltip>
                  {showTriggerStep && (
                    <Tooltip tooltip={`if not accepted — ${triggerStep.type} step: ${triggerStep.state}`}>
                      <span className="flex shrink-0 items-center gap-0.5 text-red-500 dark:text-red-400">
                        <General.XMark className="size-2.5 shrink-0 stroke-2" />
                        <StepIcon iconType={triggerStep.type} state={triggerStep.state} />
                      </span>
                    </Tooltip>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
        {actionRequired && (
          <div className="ml-1 flex -translate-x-1 items-center gap-0.5 text-xs opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100">
            <Communication.MessagePlusSquare className="size-4" />
            <span>Take action</span>
          </div>
        )}
        <div className="grow" />
        {stageDuration && (
          <Tooltip tooltip="Time in current stage" className="flex shrink-0 items-center">
            <span data-testid="stage-duration" className="text-2xs font-medium text-slate-400 dark:text-slate-500">
              {stageDuration}
            </span>
          </Tooltip>
        )}
        <ContactCardActions
          phone={phone}
          phoneVerified={phoneVerified}
          professionalEmail={professionalEmail}
          professionalEmailVerified={professionalEmailVerified}
          onCall={onCall}
        />
      </div>
    </div>
  );
}
